import { apiClient } from './apiClient';

export enum CommunicationType {
  Email = 1,
  Phone = 2,
  SMS = 3,
  InPerson = 4,
  VideoCall = 5,
  Other = 6
}

export enum CommunicationDirection {
  Incoming = 1,
  Outgoing = 2
}

export interface CommunicationLog {
  id: string;
  jobId: string;
  customerId?: string;
  type: CommunicationType;
  subject: string;
  content: string;
  communicationDate: string;
  loggedById: string;
  direction: CommunicationDirection;
  externalReference: string;
  loggedByName: string;
  customerName: string;
  jobNumber: string;
}

export interface CreateCommunicationLogDto {
  jobId: string;
  customerId?: string;
  type: CommunicationType;
  subject: string;
  content: string;
  communicationDate?: string;
  direction: CommunicationDirection;
  externalReference?: string;
}

export interface UpdateCommunicationLogDto {
  subject: string;
  content: string;
  communicationDate: string;
  direction: CommunicationDirection;
  externalReference?: string;
}

// Helper function to get type display name
export const getTypeDisplayName = (type: CommunicationType | string | number): string => {
  const typeMap: Record<number | string, string> = {
    [CommunicationType.Email]: 'Email',
    [CommunicationType.Phone]: 'Phone',
    [CommunicationType.SMS]: 'SMS',
    [CommunicationType.InPerson]: 'In Person',
    [CommunicationType.VideoCall]: 'Video Call',
    [CommunicationType.Other]: 'Other',
    'Email': 'Email',
    'Phone': 'Phone',
    'SMS': 'SMS',
    'InPerson': 'In Person',
    'VideoCall': 'Video Call',
    'Other': 'Other'
  };
  return typeMap[type] || 'Unknown';
};

// Helper function to get direction display name
export const getDirectionDisplayName = (direction: CommunicationDirection | string | number): string => {
  const directionMap: Record<number | string, string> = {
    [CommunicationDirection.Incoming]: 'Incoming',
    [CommunicationDirection.Outgoing]: 'Outgoing',
    'Incoming': 'Incoming',
    'Outgoing': 'Outgoing'
  };
  return directionMap[direction] || 'Unknown';
};

// Helper function to get type icon name for MUI icons
export const getTypeIconName = (type: CommunicationType | string | number): string => {
  const typeIconMap: Record<number | string, string> = {
    [CommunicationType.Email]: 'Email',
    [CommunicationType.Phone]: 'Phone',
    [CommunicationType.SMS]: 'Sms',
    [CommunicationType.InPerson]: 'Person',
    [CommunicationType.VideoCall]: 'VideoCall',
    [CommunicationType.Other]: 'MoreHoriz',
    'Email': 'Email',
    'Phone': 'Phone',
    'SMS': 'Sms',
    'InPerson': 'Person',
    'VideoCall': 'VideoCall',
    'Other': 'MoreHoriz'
  };
  return typeIconMap[type] || 'MoreHoriz';
};

// Normalize CommunicationType from API response (string/number) to enum
export const normalizeCommunicationType = (value: CommunicationType | string | number): CommunicationType => {
  if (typeof value === 'number') {
    return value as CommunicationType;
  }

  if (typeof value === 'string') {
    switch (value.toLowerCase()) {
      case 'email':
      case '1':
        return CommunicationType.Email;
      case 'phone':
      case '2':
        return CommunicationType.Phone;
      case 'sms':
      case '3':
        return CommunicationType.SMS;
      case 'inperson':
      case 'in_person':
      case '4':
        return CommunicationType.InPerson;
      case 'videocall':
      case 'video_call':
      case '5':
        return CommunicationType.VideoCall;
      case 'other':
      case '6':
        return CommunicationType.Other;
      default:
        return CommunicationType.Other;
    }
  }

  return CommunicationType.Other;
};

// Normalize CommunicationDirection from API response (string/number) to enum
export const normalizeCommunicationDirection = (value: CommunicationDirection | string | number): CommunicationDirection => {
  if (typeof value === 'number') {
    return value as CommunicationDirection;
  }

  if (typeof value === 'string') {
    switch (value.toLowerCase()) {
      case 'incoming':
      case '1':
        return CommunicationDirection.Incoming;
      case 'outgoing':
      case '2':
        return CommunicationDirection.Outgoing;
      default:
        return CommunicationDirection.Incoming;
    }
  }

  return CommunicationDirection.Incoming;
};

// Transform raw API CommunicationLog to properly typed CommunicationLog
export const transformCommunicationLog = (raw: any): CommunicationLog => {
  return {
    ...raw,
    type: normalizeCommunicationType(raw.type),
    direction: normalizeCommunicationDirection(raw.direction)
  };
};

class CommunicationLogService {
  /**
   * Get communication logs for a job
   */
  async getCommunicationLogsByJob(jobId: string): Promise<CommunicationLog[]> {
    const response = await apiClient.get<any[]>(`/communicationlogs/job/${jobId}`);
    return response.data.map(transformCommunicationLog);
  }

  /**
   * Get communication logs for a customer
   */
  async getCommunicationLogsByCustomer(customerId: string): Promise<CommunicationLog[]> {
    const response = await apiClient.get<any[]>(`/communicationlogs/customer/${customerId}`);
    return response.data.map(transformCommunicationLog);
  }

  /**
   * Create a new communication log
   */
  async createCommunicationLog(dto: CreateCommunicationLogDto): Promise<CommunicationLog> {
    const response = await apiClient.post<any>('/communicationlogs', dto);
    return transformCommunicationLog(response.data);
  }

  /**
   * Update an existing communication log
   */
  async updateCommunicationLog(id: string, dto: UpdateCommunicationLogDto): Promise<CommunicationLog> {
    const response = await apiClient.put<any>(`/communicationlogs/${id}`, dto);
    return transformCommunicationLog(response.data);
  }

  /**
   * Delete a communication log
   */
  async deleteCommunicationLog(id: string): Promise<void> {
    await apiClient.delete(`/communicationlogs/${id}`);
  }

  /**
   * Get a single communication log by ID
   */
  async getCommunicationLog(id: string): Promise<CommunicationLog> {
    const response = await apiClient.get<any>(`/communicationlogs/${id}`);
    return transformCommunicationLog(response.data);
  }
}

export const communicationLogService = new CommunicationLogService();
